import {
  createRoute,
  notFound,
  Outlet,
  redirect,
  retainSearchParams,
} from "@tanstack/react-router";
import { RelocationRecordScreen } from "@/app/Relocations/Screens/RelocationRecordScreen";
import { RelocationStatus, RelocationType } from "@/gql/graphql";
import { GapRentalTableScreen } from "@/app/GapRentals/Screens/GapRentalTableScreen";
import { GapRentalRecordScreen } from "@/app/GapRentals/Screens/GapRentalRecordScreen";
import { GapRentalDetailsScreen } from "@/app/GapRentals/Screens/GapRentalDetailsScreen";
import { RelocationDetailsScreen } from "@/app/Relocations/Screens/RelocationDetailsScreen";
import { RelocationWaitlistScreen } from "@/app/Relocations/Screens/RelocationWaitlistScreen";
import { RelocationAdsScreen } from "@/app/Relocations/Screens/RelocationAdsScreen";
import { RelocationActivityScreen } from "@/app/Relocations/Screens/RelocationActivityScreen";
import { RelocationListScreen } from "@/app/Relocations/Screens/RelocationListScreen";
import { z } from "zod";
import { RelocationLinesScreen } from "@/app/Relocations/Screens/RelocationLinesScreen";
import { RelocationCreateScreen } from "@/app/Relocations/Screens/RelocationCreateScreen";
import { appLayoutRoute } from "@/routes/layout-routes";
import {
  fetchQuery,
  prefetchInfiniteQuery,
  prefetchQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { relocationRecordQuery } from "@/app/Relocations/GraphQL/relocationRecordQuery";
import { RelocationBookingScreen } from "@/app/Relocations/Screens/RelocationBookingScreen";
import { RelocationTableScreen } from "@/app/Relocations/Screens/RelocationTableScreen";
import { getRelocationListQueryVariables } from "@/app/Relocations/Utils/getRelocationListQueryVariables";
import { relocationListQuery } from "@/app/Relocations/GraphQL/relocationListQuery";
import { RelocationNotFoundScreen } from "@/app/Relocations/Screens/RelocationNotFoundScreen";
import { RelocationImageGenerationScreen } from "@/app/Relocations/Screens/RelocationImageGenerationScreen";
import { RelocationProDriversScreen } from "@/app/Relocations/Screens/RelocationProDriversScreen";
import {
  getRelocationTableVariables,
  relocationTableDefaultSorting,
} from "@/app/Dashboard/Utils/getRelocationTableVariables";
import { createQueryVariables } from "@/lib/Components/DataTable/utils/data-table-utils";
import { relocationStatusCountsQuery } from "@/app/Relocations/GraphQL/relocationStatusCountsQuery";
import { loadRelocationDetails } from "@/app/Relocations/Utils/loadRelocationDetails";
import { isRecordId } from "@/routes/recordId";
import { supplierParamToId } from "@/app/Suppliers/Utils/globalSupplier";
import {
  relocationTableSearchKeys,
  relocationTableSearchParams,
  relocationTableStateFromSearch,
} from "@/app/Relocations/Utils/relocationTableSearchParams";

const relocationsSearchParams = relocationTableSearchParams.extend({
  relocationSearch: z.string().optional(),
  highlightImport: z.boolean().optional().catch(undefined),
});

export type RelocationListSearchParams = z.infer<
  typeof relocationsSearchParams
>;

export const relocationsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/relocations",
  component: Outlet,
  validateSearch: relocationsSearchParams,
  search: {
    middlewares: [retainSearchParams(relocationTableSearchKeys)],
  },
  head: () => ({
    meta: [
      {
        title: "Relocations - Imoova Admin",
      },
    ],
  }),
});

export const relocationTableRoute = createRoute({
  path: "/",
  getParentRoute: () => relocationsRoute,
  component: RelocationTableScreen,
  loaderDeps: ({ search }) => search,
  loader: ({ deps, params }) => {
    const supplierId = supplierParamToId(params.supplierId);
    const state = relocationTableStateFromSearch(deps);
    const queryVariables = createQueryVariables(
      "relocations",
      getRelocationTableVariables({
        supplierId: supplierId ?? state.supplierId,
        status: state.statuses,
        type: [RelocationType.Relocation],
        regions: state.regions,
        departureCityIds: state.departureCityIds,
        deliveryCityIds: state.deliveryCityIds,
      }),
      relocationTableDefaultSorting,
      { page: state.page, search: state.search },
    );

    prefetchQuery(
      relocationStatusCountsQuery,
      {
        supplierId: supplierId ?? undefined,
        type: [RelocationType.Relocation],
      },
      params.orgId,
    );

    return fetchQuery(relocationListQuery, queryVariables, params.orgId);
  },
  head: () => ({
    meta: [
      {
        title: "Relocations - Imoova Admin",
      },
      {
        name: "description",
        content: "Manage relocations",
      },
    ],
  }),
});

export const gapRentalsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/gap-rentals",
  component: Outlet,
  validateSearch: relocationsSearchParams,
  search: {
    middlewares: [retainSearchParams(relocationTableSearchKeys)],
  },
  head: () => ({
    meta: [
      {
        title: "Gap Rentals - Imoova Admin",
      },
    ],
  }),
});

export const gapRentalTableRoute = createRoute({
  path: "/",
  getParentRoute: () => gapRentalsRoute,
  component: GapRentalTableScreen,
  loaderDeps: ({ search }) => search,
  loader: ({ deps, params }) => {
    const supplierId = supplierParamToId(params.supplierId);
    const state = relocationTableStateFromSearch(deps);
    const queryVariables = createQueryVariables(
      "gap-rentals",
      getRelocationTableVariables({
        supplierId: supplierId ?? state.supplierId,
        status: state.statuses,
        type: [RelocationType.GapRental],
        regions: state.regions,
        departureCityIds: state.departureCityIds,
      }),
      relocationTableDefaultSorting,
      { page: state.page, search: state.search },
    );

    prefetchQuery(
      relocationStatusCountsQuery,
      {
        supplierId: supplierId ?? undefined,
        type: [RelocationType.GapRental],
      },
      params.orgId,
    );

    return fetchQuery(relocationListQuery, queryVariables, params.orgId);
  },
  head: () => ({
    meta: [
      {
        title: "Gap Rentals - Imoova Admin",
      },
      {
        name: "description",
        content: "Manage gap rentals",
      },
    ],
  }),
});

export const gapRentalRoute = createRoute({
  getParentRoute: () => gapRentalsRoute,
  path: "$relocationId",
  beforeLoad: ({ params }) => {
    if (!isRecordId(params.relocationId)) {
      throw notFound();
    }
  },
  component: GapRentalRecordScreen,
  loader: async ({ params }) => {
    const res = await fetchQuery(
      relocationRecordQuery,
      { id: params.relocationId },
      params.orgId,
    );

    if (res.relocation === null) {
      throw redirect({
        from: "/$tenant/$orgId/supplier/{-$supplierId}",
        to: "/$tenant/$orgId/supplier/{-$supplierId}/relocations/not-found",
        params: (prev) => prev,
      });
    }

    if (res.relocation.type !== RelocationType.GapRental) {
      throw redirect({
        from: "/$tenant/$orgId/supplier/{-$supplierId}",
        to: "/$tenant/$orgId/supplier/{-$supplierId}/relocations/$relocationId",
        params: (prev) => ({ ...prev, relocationId: params.relocationId }),
      });
    }

    return res;
  },
  head: ({ loaderData }) => ({
    meta: [
      {
        title: `${loaderData?.relocation?.reference ?? "Gap Rental"} - Imoova Admin`,
      },
    ],
  }),
});

export const gapRentalDetailsRoute = createRoute({
  getParentRoute: () => gapRentalRoute,
  path: "/",
  component: GapRentalDetailsScreen,
  loader: ({ params }) =>
    loadRelocationDetails(params.relocationId, params.orgId),
});

export const gapRentalBookingsRoute = createRoute({
  getParentRoute: () => gapRentalRoute,
  path: "/bookings",
  component: RelocationBookingScreen,
});

export const gapRentalWaitlistRoute = createRoute({
  getParentRoute: () => gapRentalRoute,
  path: "/waitlists",
  component: RelocationWaitlistScreen,
});

export const gapRentalActivityRoute = createRoute({
  getParentRoute: () => gapRentalRoute,
  path: "/activity",
  component: RelocationActivityScreen,
});

export const gapRentalAdsRoute = createRoute({
  getParentRoute: () => gapRentalRoute,
  path: "/ads",
  component: RelocationAdsScreen,
});

export const gapRentalGenerationRoute = createRoute({
  getParentRoute: () => gapRentalRoute,
  path: "/generations",
  component: RelocationImageGenerationScreen,
});

export const relocationRecordLayout = createRoute({
  getParentRoute: () => relocationsRoute,
  id: "_relocationRecordLayout",
  component: RelocationListScreen,
  loaderDeps: ({ search }) => search,
  loader: ({ deps, params }) => {
    const supplierId = supplierParamToId(params.supplierId);
    const variables = getRelocationListQueryVariables(deps, supplierId);
    return prefetchInfiniteQuery(relocationListQuery, variables, params.orgId);
  },
});

export const relocationNotFoundRoute = createRoute({
  getParentRoute: () => relocationRecordLayout,
  path: "/not-found",
  component: RelocationNotFoundScreen,
  head: () => ({
    meta: [
      {
        title: "Relocation Not Found - Imoova Admin",
      },
    ],
  }),
});

export const relocationCreateRoute = createRoute({
  getParentRoute: () => relocationRecordLayout,
  path: "/create",
  component: RelocationCreateScreen,
  head: () => ({
    meta: [
      {
        title: "Create Relocation - Imoova Admin",
      },
    ],
  }),
});

export const relocationRoute = createRoute({
  getParentRoute: () => relocationRecordLayout,
  path: "$relocationId",
  beforeLoad: ({ params }) => {
    if (!isRecordId(params.relocationId)) {
      throw notFound();
    }
  },
  component: RelocationRecordScreen,
  loader: async ({ params }) => {
    const res = await fetchQuery(
      relocationRecordQuery,
      { id: params.relocationId },
      params.orgId,
    );

    if (res.relocation === null) {
      throw redirect({
        from: "/$tenant/$orgId/supplier/{-$supplierId}",
        to: "/$tenant/$orgId/supplier/{-$supplierId}/relocations/not-found",
        params: (prev) => prev,
      });
    }

    if (res.relocation.type === RelocationType.GapRental) {
      throw redirect({
        from: "/$tenant/$orgId/supplier/{-$supplierId}",
        to: "/$tenant/$orgId/supplier/{-$supplierId}/gap-rentals/$relocationId",
        params: (prev) => ({ ...prev, relocationId: params.relocationId }),
      });
    }

    return res;
  },
  head: ({ loaderData }) => ({
    meta: [
      {
        title: `${loaderData?.relocation?.reference ?? "Relocation"} - Imoova Admin`,
      },
    ],
  }),
});

export const relocationDetailsRoute = createRoute({
  getParentRoute: () => relocationRoute,
  path: "/",
  component: RelocationDetailsScreen,
  loader: ({ params }) =>
    loadRelocationDetails(params.relocationId, params.orgId),
});

export const relocationLinesRoute = createRoute({
  getParentRoute: () => relocationRoute,
  path: "/lines",
  component: RelocationLinesScreen,
});

export const relocationWaitlistRoute = createRoute({
  getParentRoute: () => relocationRoute,
  path: "/waitlists",
  component: RelocationWaitlistScreen,
});

export const relocationAdsRoute = createRoute({
  getParentRoute: () => relocationRoute,
  path: "/ads",
  component: RelocationAdsScreen,
});

export const relocationActivityRoute = createRoute({
  getParentRoute: () => relocationRoute,
  path: "/activity",
  component: RelocationActivityScreen,
});

export const relocationBookingsRoute = createRoute({
  getParentRoute: () => relocationRoute,
  path: "/bookings",
  component: RelocationBookingScreen,
});

export const relocationGenerationRoute = createRoute({
  getParentRoute: () => relocationRoute,
  path: "/generations",
  component: RelocationImageGenerationScreen,
});

export const relocationProDriversRoute = createRoute({
  getParentRoute: () => relocationRoute,
  path: "/pro-drivers",
  component: RelocationProDriversScreen,
});
